import { ActionReducerMapBuilder, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { withLoadingReducer } from 'utils/reducerHandler'
import { LocationsType } from 'features/Location/services/location'
import {
  GetSOScheduleByIdResponseType,
  GetSupplyOrderRemarksResponseType,
  GetWimsConstantsResponseType,
} from '../../services/soSchedules'
import {
  SLICE_NAME,
  fetchGetAllLocations,
  fetchGetSupplyOrderRemarks,
  fetchGetWimsConstants,
  fetchGetSOScheduleById,
  fetchPostSOSchedule,
  fetchPutSOScheduleById,
  fetchGetSOScheduleDestinations,
} from './thunk'

export type DayType = 'Senin' | 'Selasa' | 'Rabu' | 'Kamis' | 'Jumat' | 'Sabtu' | 'Minggu'

function deriveScheduleType(
  startDate: string,
  endDate: string,
): SOSchedulesAddEditSliceType['data'][number]['schedule_type'] {
  if (!startDate && !endDate) return 'ALL'
  if (!startDate) return 'IMMEDIATELY_ACTIVE'
  if (!endDate) return 'UNLIMITED'
  return 'DEFAULT'
}

const DAY_LABEL_MAP: Record<string, DayType> = {
  Monday: 'Senin',
  Tuesday: 'Selasa',
  Wednesday: 'Rabu',
  Thursday: 'Kamis',
  Friday: 'Jumat',
  Saturday: 'Sabtu',
  Sunday: 'Minggu',
}

export type SOSchedulesAddEditSliceType = {
  isLoading: boolean
  constants: {
    remarks: GetSupplyOrderRemarksResponseType['data']
    locations: LocationsType[]
    schedule_types: GetWimsConstantsResponseType['data']['schedule_types']
  }
  detail: Nullable<GetSOScheduleByIdResponseType['data']>
  data: {
    id: number
    location_id: number
    location_name: string
    location_type: string
    schedule_start: string
    schedule_end: string
    schedule_type: 'DEFAULT' | 'IMMEDIATELY_ACTIVE' | 'UNLIMITED' | 'ALL'
    is_checked: boolean
  }[]
  originalData: SOSchedulesAddEditSliceType['data']
  dirtyRows: number[]
  isBulkDirty: boolean
  form: {
    schedule_types: Nullable<GetWimsConstantsResponseType['data']['schedule_types'][number]>
    days: DayType[]
    times: string[]
    remarks: Nullable<GetSupplyOrderRemarksResponseType['data'][number]>
    origin: Nullable<LocationsType>
  }
  popup: {
    destination: {
      isOpen: boolean
      checkeds: LocationsType[]
    }
  }
}

const initialState: SOSchedulesAddEditSliceType = {
  isLoading: false,
  constants: {
    remarks: [],
    locations: [],
    schedule_types: [],
  },
  detail: null,
  data: [],
  originalData: [],
  dirtyRows: [],
  isBulkDirty: false,
  form: {
    schedule_types: null,
    days: [],
    times: [],
    remarks: null,
    origin: null,
  },
  popup: {
    destination: {
      isOpen: false,
      checkeds: [],
    },
  },
}

const soSchedulesAddEditSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset: () => initialState,
    resetData: (state) => {
      state.data = []
    },
    resetPopupDestinationChecked: (state) => {
      state.popup.destination.checkeds = []
    },
    setForm: (state, { payload }: PayloadAction<Partial<SOSchedulesAddEditSliceType['form']>>) => {
      state.form = {
        ...state.form,
        ...payload,
      }
    },
    setPopupDestination: (state, action: PayloadAction<{ isOpen: boolean }>) => {
      state.popup.destination.isOpen = action.payload.isOpen
    },
    setPopupDestinationCheckItem: (state, { payload }: PayloadAction<LocationsType>) => {
      const index = state.popup.destination.checkeds.findIndex(
        (item) => item.location_id === payload.location_id,
      )
      if (index === -1) {
        state.popup.destination.checkeds.push(payload)
      } else {
        state.popup.destination.checkeds.splice(index, 1)
      }
    },
    setPopupDestinationCheckAll: (state, { payload }: PayloadAction<LocationsType[]>) => {
      const allChecked = payload.every((item) =>
        state.popup.destination.checkeds.some((c) => c.location_id === item.location_id),
      )
      if (allChecked) {
        state.popup.destination.checkeds = state.popup.destination.checkeds.filter(
          (c) => !payload.some((item) => item.location_id === c.location_id),
        )
      } else {
        const toAdd = payload.filter(
          (item) =>
            !state.popup.destination.checkeds.some((c) => c.location_id === item.location_id),
        )
        state.popup.destination.checkeds.push(...toAdd)
      }
    },
    deleteCheckedData: (state) => {
      state.data = state.data.filter((item) => !item.is_checked)
    },
    deleteDataByLocationId: (state, { payload }: PayloadAction<number>) => {
      state.data = state.data.filter((item) => item.location_id !== payload)
    },
    setData: (state, { payload }: PayloadAction<LocationsType[]>) => {
      const existingIds = new Set(state.data.map((item) => item.location_id))
      const newItems = payload
        .filter((item) => !existingIds.has(item.location_id))
        .map((item) => ({
          ...item,
          id: 0,
          schedule_start: '',
          schedule_end: '',
          schedule_type: 'DEFAULT' as const,
          is_checked: false,
        }))
      state.data.unshift(...newItems)
    },
    setDataCheckItem: (state, { payload }: PayloadAction<number>) => {
      const item = state.data.find((d) => d.location_id === payload)
      if (item) {
        item.is_checked = !item.is_checked
      }
    },
    setDataCheckAll: (state) => {
      const allChecked = state.data.every((d) => d.is_checked)

      state.data.forEach((item) => {
        // eslint-disable-next-line no-param-reassign
        item.is_checked = !allChecked
      })
    },
    uncheckAll: (state) => {
      state.data.forEach((item) => {
        // eslint-disable-next-line no-param-reassign
        item.is_checked = false
      })
    },
    restoreDataSchedule: (
      state,
      {
        payload,
      }: PayloadAction<{
        index: number
        schedule_start: string
        schedule_end: string
        schedule_type: 'DEFAULT' | 'IMMEDIATELY_ACTIVE' | 'UNLIMITED' | 'ALL'
      }>,
    ) => {
      const item = state.data[payload.index]
      if (item) {
        item.schedule_start = payload.schedule_start
        item.schedule_end = payload.schedule_end
        item.schedule_type = payload.schedule_type
      }
    },
    setRowDirty: (state, { payload }: PayloadAction<number>) => {
      if (!state.dirtyRows.includes(payload)) {
        state.dirtyRows.push(payload)
      }
    },
    clearRowDirty: (state, { payload }: PayloadAction<number>) => {
      state.dirtyRows = state.dirtyRows.filter((id) => id !== payload)
    },
    setBulkDirty: (state) => {
      state.isBulkDirty = true
    },
    clearBulkDirty: (state) => {
      state.isBulkDirty = false
    },
    setDataSchedule: (
      state,
      {
        payload,
      }: PayloadAction<{
        index: number
        value: string
        type: 'DEFAULT' | 'IMMEDIATELY_ACTIVE' | 'UNLIMITED' | 'ALL'
        field?: 'start' | 'end'
      }>,
    ) => {
      const { index, value, type, field } = payload
      const item = state.data[index]
      if (item) {
        item.schedule_type = type
        if (field === 'start') {
          item.schedule_start = value
        } else if (field === 'end') {
          item.schedule_end = value
        } else if (type === 'UNLIMITED' || type === 'ALL') {
          item.schedule_start = value
          item.schedule_end = ''
        } else {
          const [start, end] = value.split(' - ')
          item.schedule_start = start || ''
          item.schedule_end = end || ''
        }
      }
    },
  },
  extraReducers: withLoadingReducer(
    (builder: ActionReducerMapBuilder<SOSchedulesAddEditSliceType>) => {
      builder.addCase(fetchGetAllLocations.fulfilled, (state, { payload }) => {
        state.constants.locations = payload
      })
      builder.addCase(fetchGetSupplyOrderRemarks.fulfilled, (state, { payload }) => {
        state.constants.remarks = payload.data
      })
      builder.addCase(fetchGetWimsConstants.fulfilled, (state, { payload }) => {
        state.constants.schedule_types = payload.data.schedule_types
      })
      builder
        .addCase(fetchGetSOScheduleById.fulfilled, (state, { payload }) => {
          state.detail = payload.data

          const { type, schedule_days, schedule_time, so_remarks, location } = payload.data
          state.form = {
            schedule_types: state.constants.schedule_types.find((s) => s.key === type) ?? null,
            days: schedule_days
              .split('/')
              .map((d) => DAY_LABEL_MAP[d])
              .filter((d): d is DayType => !!d),
            times: schedule_time.split('/'),
            remarks: state.constants.remarks.find((r) => r.description === so_remarks) ?? null,
            origin: {
              location_id: Number(location.id),
              location_name: location.name,
              location_type: location.type,
            },
          }
        })
        .addCase(fetchGetSOScheduleDestinations.fulfilled, (state, { payload }) => {
          const mapped = (payload?.data ?? []).map((item) => ({
            id: item.id,
            location_id: Number(item.location.id),
            location_name: item.location.name,
            location_type: item.location.type,
            schedule_start: item.start_date,
            schedule_end: item.end_date,
            schedule_type: deriveScheduleType(item.start_date, item.end_date),
            is_checked: false,
          }))
          state.data = mapped
          state.originalData = mapped
        })
    },
    [
      fetchGetSOScheduleById,
      fetchGetAllLocations,
      fetchGetSupplyOrderRemarks,
      fetchGetWimsConstants,
      fetchPostSOSchedule,
      fetchPutSOScheduleById,
      fetchGetSOScheduleDestinations,
    ],
  ),
})

export const {
  reset,
  resetData,
  deleteCheckedData,
  deleteDataByLocationId,
  setData,
  setForm,
  setDataSchedule,
  restoreDataSchedule,
  setDataCheckItem,
  setDataCheckAll,
  uncheckAll,
  setPopupDestination,
  resetPopupDestinationChecked,
  setPopupDestinationCheckItem,
  setPopupDestinationCheckAll,
  setRowDirty,
  clearRowDirty,
  setBulkDirty,
  clearBulkDirty,
} = soSchedulesAddEditSlice.actions

export type ScheduleType = SOSchedulesAddEditSliceType['data'][number]['schedule_type']

export default soSchedulesAddEditSlice.reducer
